import { pendingTask, begin, end } from '../constants/pendingTask';
import * as actions from './actionTypes';
import { fetchFromReportApi as fetch } from './fetchFromApi';
import * as actionHelpers from '../scripts/actionHelpers';

/**
 * Load analytics report dropdown options for a client.
 * If successful, this will dispatch the LOAD_ANALYTICS_REPORT_DROPDOWN_SUCCESS action.
 * If failure, it will dispatch the LOAD_ANALYTICS_REPORT_DROPDOWN_FAILURE action.
 * @param {number} clientId The id of the client.
 * @returns {function} A function that returns a Promise.
 */
export function loadAnalyticsReportDropdown(clientId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    return fetch(`/analyticsReport/dropdown?clientId=${clientId}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (actionHelpers.isErrorResponse(data)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_ANALYTICS_REPORT_DROPDOWN_FAILURE,
            data
          );
        }
        dispatch({
          type: actions.LOAD_ANALYTICS_REPORT_DROPDOWN_SUCCESS,
          analyticsReports: data,
          [pendingTask]: end,
        });
        return data;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_ANALYTICS_REPORT_DROPDOWN_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Load clients dropdown options.
 * If successful, this will dispatch the LOAD_CLIENTS_SUCCESS action.
 * If failure, it will dispatch the LOAD_CLIENTS_FAILURE action.
 * @returns {function} A function that returns a Promise.
 */
export function loadAnalyticsTaxClientDropdown() {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    return fetch(`/analyticsReport/analyticsTaxClients`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (actionHelpers.isErrorResponse(data)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_ANALYTICS_TAX_CLIENTS_FAILURE,
            data
          );
        }
        dispatch({
          type: actions.LOAD_ANALYTICS_TAX_CLIENTS_SUCCESS,
          analyticsTaxClients: data,
          [pendingTask]: end,
        });
        return data;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_ANALYTICS_TAX_CLIENTS_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Load analytics REIT dropdown options for a client.
 * If successful, this will dispatch the LOAD_ANALYTICS_REIT_SUCCESS action.
 * If failure, it will dispatch the LOAD_ANALYTICS_REIT_FAILURE action.
 * @param {number} clientId The id of the client.
 * @returns {function} A function that returns a Promise.
 */
export function loadAnalyticsREITDropdown(clientId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    return fetch(
      `/analyticsReport/analyticsREITDropdown?clientId=${clientId}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }
    )
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (actionHelpers.isErrorResponse(data)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_ANALYTICS_REIT_FAILURE,
            data
          );
        }
        dispatch({
          type: actions.LOAD_ANALYTICS_REIT_SUCCESS,
          analyticsREITs: data,
          [pendingTask]: end,
        });
        return data;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_ANALYTICS_REIT_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Load analytics report periods dropdown options for a client.
 * If successful, this will dispatch the LOAD_ANALYTICS_REPORT_PERIODS_SUCCESS action.
 * If failure, it will dispatch the LOAD_ANALYTICS_REPORT_PERIODS_FAILURE action.
 * @param {number} clientId The id of the client.
 * @returns {function} A function that returns a Promise.
 */
export function loadAnalyticsReportPeriodsDropdown(clientId, reitIds = []) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    const reitParam =
      Array.isArray(reitIds) && reitIds.length > 0
        ? encodeURIComponent(reitIds.join(','))
        : '';
    const url =
      Array.isArray(reitIds) && reitIds.length > 0
        ? `/analyticsReport/analyticsReportPeriodDropdown?clientId=${clientId}&reitIds=${reitParam}`
        : `/analyticsReport/analyticsReportPeriodDropdown?clientId=${clientId}`;
    return fetch(url, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((response) => response.json())
      .then((data) => {
        if (actionHelpers.isErrorResponse(data)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_ANALYTICS_REPORT_PERIODS_FAILURE,
            data
          );
        }
        dispatch({
          type: actions.LOAD_ANALYTICS_REPORT_PERIODS_SUCCESS,
          analyticsReportPeriods: Array.isArray(data) ? data : [],
          [pendingTask]: end,
        });
        return data;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_ANALYTICS_REPORT_PERIODS_FAILURE,
          null,
          error
        );
      });
  };
}
